import { useQuery } from '@apollo/client/react';
import { Box, Button, Separator, Dialog, HStack, Portal, SimpleGrid, Spacer, Spinner } from '@chakra-ui/react';
import { EventCalendarDay } from '@src/components/sco/Events/EventCalendar/EventCalendarDay';
import { determineCalendarStartDate } from '@src/components/sco/Events/EventCalendar/determineCalendarStartDate';
import { EventCard, eventCardGroupEventFragment } from '@src/components/sco/Events/EventCard';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { GroupEvent } from '@src/graphql-types';
import { clearDialogBodyLocks } from '@src/lib/clearDialogBodyLocks';
import { gql } from '@src/lib/gql';
import groupBy from 'lodash/groupBy';
import moment from 'moment';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useEffect, useState } from 'react';
type EventCalendarProps = {
  startDate?: Date | moment.Moment;
  defaultWeekCount?: number;
  isCurrentUserAttending?: boolean;
  isFuture?: boolean;
  order?: string[][];
  search?: string;
  topicKeys?: string[];
  typeKeys?: string[];
};
export const EventCalendar = (props: EventCalendarProps) => {
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const isMobile = getBreakpointValue({
    base: true,
    lg: false
  });
  const startDate = props.startDate ? moment(props.startDate) : determineCalendarStartDate(moment());
  const endDate = moment(startDate).endOf('week').add(props.defaultWeekCount || 2, 'week');
  const [dateRange, setDateRange] = React.useState<moment.Moment[]>([startDate, endDate]);
  const [cachedQueryData, setCachedQueryData] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState<GroupEvent | null>(null);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [search] = useQueryState('search', parseAsString);
  const handleShowEventDetails = (event: GroupEvent) => {
    setSelectedEvent(event);
    setIsEventDialogOpen(true);
  };
  const handleEventDialogOpenChange = ({
    open
  }: {
    open: boolean;
  }) => {
    setIsEventDialogOpen(open);
    if (!open) {
      requestAnimationFrame(() => clearDialogBodyLocks());
    }
  };
  const query = useQuery(gql`
      query (
        $maximumStartsAt: DateTime,
        $minimumStartsAt: DateTime,
        $order: [[String]],
        $page: Int,
        $pageSize: Int,
        $search: String
        $topicKeys: [String],
        $typeKeys: [String],
      ) {
        groupEvents(
          maximumStartsAt: $maximumStartsAt,
          minimumStartsAt: $minimumStartsAt,
          order: $order
          page: $page
          pageSize: $pageSize
          search: $search
          topicKeys: $topicKeys
          typeKeys: $typeKeys
        ) {
          id
          items {
            id
            ${eventCardGroupEventFragment}
          }
          pagination {
            hasNextPage
            itemCount
            page
            pageCount
            pageSize
          }
        }
      }
    `, {
    variables: {
      isCurrentUserAttending: props.isCurrentUserAttending,
      isFuture: props.isFuture,
      maximumStartsAt: dateRange[1],
      minimumStartsAt: dateRange[0],
      search: props.search || search || null,
      order: props.order || [['startsAt', 'ASC'], ['title', 'asc']],
      page: 1,
      pageSize: 100,
      topicKeys: props.topicKeys,
      typeKeys: props.typeKeys
    }
  });
  useEffect(() => {
    if (query.data) {
      setCachedQueryData(query.data);
    }
  }, [query.data]);
  const loadMore = () => {
    setDateRange([dateRange[0], moment(dateRange[1]).add(1, 'week')]);
  };
  const items = (query.data as any)?.groupEvents?.items || cachedQueryData?.groupEvents?.items || [];
  const eventsGroupedByDay = groupBy(items, event_0 => moment(event_0.startsAt).format('YYYY-MM-DD'));
  if (items && items.length > 0) {
    const day = moment(startDate);
    while (day.isBefore(endDate)) {
      if (day.isoWeekday() <= 5) {
        // M=1, T=2, W=3, Th=4, F=5
        if (!eventsGroupedByDay[day.format('YYYY-MM-DD')]) {
          eventsGroupedByDay[day.format('YYYY-MM-DD')] = [];
        }
      }
      day.add(1, 'day');
    }
  }
  return <>
      <SimpleGrid columns={[1, 1, 1, 1, 5]} gap={'4px'}>
        {Object.keys(eventsGroupedByDay).sort().map(dayDateString => {
        const isMobileAndPastDay = isMobile && moment(dayDateString).isBefore(moment().startOf('day'));
        if (isMobileAndPastDay) {
          return <></>;
        }
        return <EventCalendarDay key={dayDateString} date={moment(dayDateString)} events={eventsGroupedByDay[dayDateString] || []} onShowEventDetails={handleShowEventDetails} />;
      })}
      </SimpleGrid>
      <Spacer height={'1.5em'} />
      <HStack>
        <Separator borderColor={'action.primary'} flex={'1'} variant={'dashed'} />
        <Box>
          <Button size={'sm'} onClick={loadMore}>
            {query.loading ? <Spinner size={'xs'} /> : <>Show Next Week</>}
          </Button>
        </Box>
        <Separator borderColor={'action.primary'} flex={'1'} variant={'dashed'} />
      </HStack>
      <Portal>
        <Dialog.Root open={isEventDialogOpen} onOpenChange={handleEventDialogOpenChange}>
          <Dialog.Backdrop backdropFilter={'blur(2px)'} bgColor={'blackAlpha.600'} />
          <Dialog.Positioner>
            <Dialog.Content bgColor={'bg.DEFAULT'} borderRadius={'20px'}>
              <Dialog.CloseTrigger bgColor={'bg.DEFAULT'} borderRadius={'full'} />
              {selectedEvent && <EventCard descriptionHeightInPx={100} event={selectedEvent} fluidHero={true} showDescription={true} />}
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      </Portal>
    </>;
};